import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import config from '../../config.js';

import PageTable from '../../components/general/pageTable.js';
import EditRequestForm from '../forms/editRequestForm.js';

import '../../styles/global.css';
import '../../styles/buttons.css';
import '../../styles/popup.css';
import './styles/detailedAccount.css';

import EditUserForm from '../forms/editUserForm';
import EditPropertyForm from '../forms/editPropertyForm';

import imageUpload from '../general/imageUpload';
import { Upload } from 'antd';

const DetailedAccountPanel = ({ selectedPerson, account, property, propertyRoleId, role, onClose }) => {
  const [isEditUserOpen, setIsEditUserOpen] = useState(false);
  const [isEditPropertyOpen, setIsEditPropertyOpen] = useState(false);
  const [user, setUser] = useState(account?.user || null);

  const [editAssignedRequest, setEditAssignedRequest] = useState(null);
  const [isEditAssignedOpen, setIsEditAssignedOpen] = useState(false);
  const [assignedRequests, setAssignedRequests] = useState([]);
  
  const [profileImage, setProfileImage] = useState(null);
  
  const isStaff = Boolean(role && role.toLowerCase().startsWith('staff'));

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axios.get(
          `${config.API_URL}/v2/user/email/${account.user.email}`,
          {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}}
        );
        setUser(res.data);
      } catch (err) {
        console.error("❌ Failed to fetch user details:", err);
      }
    };
    if (account?.user?.email) {
      fetchUser();
    }
  }, [account?.user?.email]);

  // Fetch assigned requests into local state
  useEffect(() => {
    if (isStaff && account?._id) {
      const fetchAssignedRequests = async () => {
        try {
          const res = await axios.get(
            `${config.API_URL}/request/assignee/${selectedPerson.account._id}`,
            {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}}
          );
          setAssignedRequests(res.data.filter(r => r.status !== 'Completed'));
        } catch (err) {
          console.error('❌ Failed to fetch assigned requests:', err);
        }
      };
      fetchAssignedRequests();
    }
  }, [isStaff, account?._id, selectedPerson?.account._id]);

  useEffect(() => {
    if (user?.profilePicture) {
      if (typeof user.profilePicture === "object" && user.profilePicture.key) {
        setProfileImage(user.profilePicture.key);
      } else if (typeof user.profilePicture === "string") {
      setProfileImage(user.profilePicture);
      }
    } else {
      setProfileImage(null);
    }
  }, [user]);

  const handleRequestUpdated = (updatedRequest) => {
    setAssignedRequests(prev =>
      prev.map(req => (req._id === updatedRequest._id ? updatedRequest : req))
    );
    setIsEditAssignedOpen(false);
    setEditAssignedRequest(null);
  };

  if (!account?.user) return null;

  return (
    <>
      <div className="popup-overlay" onClick={onClose}></div>
      <div className="account-details-panel">
        <div className="detail-row" style={{ gap: '10px' }}>
          <div className='detail-column' style={{ display: 'flex', flex: '0.5 1 0%', marginLeft: '20px', flexDirection: 'column', alignItems: 'center' }}>
            {profileImage ? (
              <img
                src={profileImage}
                alt="profile"
                className='profile-picture'
                style={{ objectFit: 'cover' }}
              />
            ) : (
              <img
                src="/default-user.png" // from the public folder
                alt="Default Profile"
                className='profile-picture'
                style={{ objectFit: 'cover' }}
                />
            )}

            <Upload
              name="avatar"
              className="profile-uploader"
              showUploadList={false}
              beforeUpload={async (file) => {
                const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
                if (!isJpgOrPng) {
                  toast.error('You can only upload JPG/PNG file!');
                  return Upload.LIST_IGNORE;
                }

                const isLt2M = file.size / 1024 / 1024 < 2;
                if (!isLt2M) {
                  toast.error('Image must be smaller than 2MB!');
                  return Upload.LIST_IGNORE;
                }

                try {
                  const uploadedIds = await imageUpload(
                    [file].map(f => ({ name: f.name, type: f.type, data: f})),
                    'profile',
                    property._id,
                    account._id,
                    ['profile picture']
                  );

                  const uploadedId = uploadedIds[0];

                  await axios.put(
                    `${config.API_URL}/v2/user/email/${user.email}`,
                    { profilePicture: uploadedId },
                    {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}}
                  );

                  setProfileImage(uploadedId.key);
                  toast.success('Profile picture updated successfully!');
                } catch (err) {
                  console.error('❌ Error uploading profile picture:', err);
                  toast.error('Failed to upload profile picture.');
                }

                return false;
              }}
              accept="image/png, image/jpeg"
            >
              <button className="header-button">Upload Photo</button>
            </Upload>
          </div>

          <div className="detail-column">
            <h2>{`${user.givenName} ${user.middleName || ''} ${user.lastName}`.trim()}</h2>
            <h3>{role.charAt(0).toUpperCase() + role.slice(1)}</h3>
            <p className="detail" style={{ marginBottom: '0px' }}>{property.name}</p>
            <p className="detail">{property.address.postalCode} - {property.address.street}</p>
          </div>
        </div>

        {/* Details Section */}
        <div className="account-section">
          <div className="account-section-header">
            <span>Details</span>
            <button className="action-buttons" onClick={() => setIsEditUserOpen(true)}>
              <i className="fa-solid fa-pen-to-square"></i>
            </button>
          </div>
          <div className="account-details">
            <div className="detail-row">
              <div className="detail-column">
                <p className="detail-label">First Name</p>
                <p className="detail">{`${user.givenName} ${user.middleName}`.trim() || 'N/A'}</p>
              </div>
              <div className="detail-column">
                <p className="detail-label">Last Name</p>
                <p className="detail">{user.lastName || 'N/A'}</p>
              </div>
            </div>
            <div className="detail-row">
              <div className="detail-column">
                <p className="detail-label">Company Name</p>
                <p className="detail">{property.name || 'N/A'}</p>
              </div>
              <div className="detail-column">
                <p className="detail-label">Job Title</p>
                <p className="detail">
                  {role === 'manager'
                    ? 'Manager'
                    : role === 'owner'
                    ? 'Owner'
                    : role === 'tenant'
                    ? 'Tenant'
                    : role === 'staff-maintenance'
                    ? 'Maintenance'
                    : role === 'staff-security'
                    ? 'Security'
                    : 'Unknown'}
                </p>
              </div>
            </div>
            <div className="detail-row">
              <div className="detail-column">
                <p className="detail-label">Notes</p>
                <p className="detail" style={{ maxHeight: '50px' }}>
                  "At 28, Steve is a charismatic product designer with a flair for blending sleek aesthetics and smart tech. He's already co-founded two successful startups and teaches part-time at a design institute."
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="account-section">
          <div className="account-section-header">
            <span>Contact Information</span>
            <button className="action-buttons" onClick={() => setIsEditUserOpen(true)}>
              <i className="fa-solid fa-pen-to-square"></i>
            </button>
          </div>
          <div className="account-details">
            <div className="detail-row">
              <div className="detail-column">
                <p className="detail-label">Email</p>
                <p className="detail">{user.email || ''}</p>
              </div>
              <div className="detail-column">
                <p className="detail-label">Phone Number</p>
                <p className="detail">{user.mobileNumber || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="account-section">
          <div className="account-section-header">
            <span>Address</span>
            <button className="action-buttons" onClick={() => setIsEditPropertyOpen(true)}>
              <i className="fa-solid fa-pen-to-square"></i>
            </button>
          </div>
          <div className="account-details">
            <div className="detail-row">
              <div className="detail-column">
                <p className="detail-label">Street</p>
                <p className="detail">{property.address.street || ''}</p>
              </div>
            </div>
            <div className="detail-row">
              <div className="detail-column">
                <p className="detail-label">City/Municipality</p>
                <p className="detail">{property.address.city}</p>
              </div>
              <div className="detail-column">
                <p className="detail-label">Province/Region</p>
                <p className="detail">{property.address.state}</p>
              </div>
            </div>
            <div className="detail-row">
              <div className="detail-column">
                <p className="detail-label">Zip Code</p>
                <p className="detail">{property.address.postalCode}</p>
              </div>
              <div className="detail-column">
                <p className="detail-label">Country</p>
                <p className="detail">{property.address.country}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Staff Assigned Requests */}
        {isStaff && (
          <div className="account-section">
            <div className="account-section-header">
              <span>Assigned Requests</span>
            </div>
            <PageTable
              rows={assignedRequests}
              columns={[
                { label: 'Title', accessor: row => row.details?.title || 'N/A', width: '25%' },
                { label: 'Type', accessor: 'type', width: '25%' },
                { label: 'Status', accessor: 'status', width: '25%' },
                { label: 'Priority', accessor: row => row.details?.priority || 'High', width: '25%' },
              ]}
              hasButtons={true}
              hasEdit={true}
              hasDelete={false}
              hasExpansion={false}
              onEdit={(request) => {
                setEditAssignedRequest(request);
                setIsEditAssignedOpen(true);
              }}
            />
          </div>
        )}

        {/* Documents Section */}
        <div className="account-section">
          <div className="account-section-header">
            <span>Documents and Photos</span>
          </div>
          <div className="account-details" style={{ marginBottom: '50px' }}>
            <div className="detail-row" style={{ display: 'flex', gap: '10px' }}>
              <div className="placeholder-img"></div>
              <div className="placeholder-img"></div>
              <div className="placeholder-img"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit User Form */}
      {isEditUserOpen && (
        <EditUserForm
          user={user}
          roleId={propertyRoleId}
          onClose={() => setIsEditUserOpen(false)}
          onUserUpdated={() => setIsEditUserOpen(false)}
        />
      )}

      {/* Edit Property Form */}
      {isEditPropertyOpen && (
        <EditPropertyForm
          property={property}
          roleId={propertyRoleId}
          onClose={() => setIsEditPropertyOpen(false)}
          onPropertyUpdated={() => setIsEditPropertyOpen(false)}
        />
      )}

      {/* Edit Request Form */}
      {isEditAssignedOpen && editAssignedRequest && (
        <EditRequestForm
          requestId={editAssignedRequest._id}
          property={property}
          onClose={() => {
            setIsEditAssignedOpen(false);
            setEditAssignedRequest(null);
          }}
          onRequestUpdated={handleRequestUpdated}
        />
      )}
    </>
  );
};

export default DetailedAccountPanel;
